import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';

export default function Questionnaire() {
  const { sessionId } = useParams();
  const { t, i18n } = useTranslation();
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('loading');
  const [showRequiredError, setShowRequiredError] = useState(false);

  const lang = i18n.language?.split('-')[0] || 'id';

  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    const { data: sessionData, error: sessionError } = await supabase
      .from('assessment_sessions')
      .select('*, questionnaire_templates(*)')
      .eq('id', sessionId)
      .single();

    if (sessionError || !sessionData) {
      setStatus('not_found');
      setLoading(false);
      return;
    }

    if (sessionData.status === 'completed') {
      setStatus('completed');
      setLoading(false);
      return;
    }

    if (sessionData.status === 'expired' || (sessionData.expires_at && new Date(sessionData.expires_at) < new Date())) {
      setStatus('expired');
      setLoading(false);
      return;
    }

    setSession(sessionData);

    // Fetch questions for this template
    if (sessionData.template_id) {
      const { data: templateQuestions } = await supabase
        .from('questionnaire_template_questions')
        .select('*, questions(*)')
        .eq('template_id', sessionData.template_id)
        .order('sort_order', { ascending: true });

      if (templateQuestions) {
        const qs = templateQuestions.map((tq) => ({ ...tq.questions, weight: tq.weight }));
        setQuestions(qs);
      }
    }

    // Update session status to in_progress if pending
    if (sessionData.status === 'pending') {
      await supabase
        .from('assessment_sessions')
        .update({ status: 'in_progress', started_at: new Date().toISOString() })
        .eq('id', sessionId);
    }

    setStatus('active');
    setLoading(false);
  };

  const getLocalizedField = (obj, field) => {
    if (!obj) return '';
    return obj[`${field}_${lang}`] || obj[`${field}_id`] || obj[`${field}_en`] || '';
  };

  const handleResponse = (questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
    setShowRequiredError(false);
  };

  const isAnswered = (question) => {
    if (!question) return true;
    const value = responses[question.id];
    if (question.question_type === 'multiple_choice') {
      return Array.isArray(value) && value.length > 0;
    }
    if (question.question_type === 'boolean' || question.question_type === 'yes_no') {
      return value === true || value === false;
    }
    return value !== undefined && value !== null && value !== '';
  };

  const handleNext = () => {
    const current = questions[currentIndex];
    if (current?.is_required && !isAnswered(current)) {
      setShowRequiredError(true);
      return;
    }
    setShowRequiredError(false);
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    setShowRequiredError(false);
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    // Block submit until every required question has an answer
    const firstMissing = questions.findIndex((q) => q.is_required && !isAnswered(q));
    if (firstMissing !== -1) {
      setCurrentIndex(firstMissing);
      setShowRequiredError(true);
      return;
    }

    setSubmitting(true);

    try {
      // Save all responses
      const responseInserts = questions.map((q) => ({
        applicant_id: session.applicant_id,
        question_id: q.id,
        template_id: session.template_id,
        response_text: typeof responses[q.id] === 'string' ? responses[q.id] : null,
        response_numeric: typeof responses[q.id] === 'number' ? responses[q.id] : null,
        response_boolean: typeof responses[q.id] === 'boolean' ? responses[q.id] : null,
        response_choice: q.question_type === 'single_choice' ? responses[q.id] : null,
        response_choices: q.question_type === 'multiple_choice' ? responses[q.id] : null,
      }));

      // supabase-js returns errors rather than throwing — check them, or a
      // failed insert would lose every answer while the session is marked done.
      const { error: respError } = await supabase.from('applicant_responses').insert(responseInserts);
      if (respError) throw respError;

      // Only mark completed once the answers are safely stored
      const { error: sessError } = await supabase
        .from('assessment_sessions')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
        })
        .eq('id', sessionId);
      if (sessError) throw sessError;

      setStatus('completed');
    } catch (error) {
      console.error('Submit error:', error);
      alert(t('questionnaire.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('questionnaire.notFound')}</h2>
          <p className="text-gray-500">{t('questionnaire.notFoundMessage')}</p>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('questionnaire.expired')}</h2>
          <p className="text-gray-500">{t('questionnaire.expiredMessage')}</p>
        </div>
      </div>
    );
  }

  if (status === 'completed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('questionnaire.completed')}</h2>
          <p className="text-gray-500">{t('questionnaire.completedMessage')}</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  const renderResponseInput = (question) => {
    const value = responses[question.id];
    const options = getLocalizedField(question, 'options') || question.options_en || [];

    switch (question.question_type) {
      case 'free_text':
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleResponse(question.id, e.target.value)}
            rows={4}
            className="input-field"
            placeholder={t('questionnaire.answerPlaceholder')}
          />
        );

      case 'numeric_rating':
      case 'numeric':
        return (
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => handleResponse(question.id, e.target.value === '' ? '' : parseFloat(e.target.value))}
            className="input-field w-40"
            min="0"
            max={question.max_score || 100}
            placeholder="0"
          />
        );

      case 'boolean':
      case 'yes_no':
        return (
          <div className="flex gap-4">
            <button
              onClick={() => handleResponse(question.id, true)}
              className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${
                value === true
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {t('common.yes')}
            </button>
            <button
              onClick={() => handleResponse(question.id, false)}
              className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${
                value === false
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {t('common.no')}
            </button>
          </div>
        );

      case 'scale_5':
      case 'scale_10':
      case 'rating': {
        const count = question.question_type === 'scale_10' ? 10 : 5;
        return (
          <div className="flex flex-wrap justify-center gap-2">
            {Array.from({ length: count }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                onClick={() => handleResponse(question.id, num)}
                className={`w-11 h-11 rounded-full border-2 font-semibold transition-colors ${
                  value === num
                    ? 'border-primary-500 bg-primary-500 text-white'
                    : 'border-gray-200 text-gray-700 hover:border-gray-300'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        );
      }

      case 'single_choice':
        return (
          <div className="space-y-2">
            {(options || []).map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleResponse(question.id, option)}
                className={`w-full text-left rtl:text-right py-3 px-4 rounded-lg border-2 transition-colors ${
                  value === option
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        );

      case 'multiple_choice': {
        const selected = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {(options || []).map((option, idx) => {
              const checked = selected.includes(option);
              return (
                <button
                  key={idx}
                  onClick={() =>
                    handleResponse(
                      question.id,
                      checked ? selected.filter((o) => o !== option) : [...selected, option]
                    )
                  }
                  className={`w-full flex items-center gap-3 text-left rtl:text-right py-3 px-4 rounded-lg border-2 transition-colors ${
                    checked
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className={`w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center ${checked ? 'border-primary-500 bg-primary-500' : 'border-gray-300'}`}>
                    {checked && (
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span>{option}</span>
                </button>
              );
            })}
          </div>
        );
      }

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleResponse(question.id, e.target.value)}
            className="input-field"
            placeholder={t('questionnaire.answerPlaceholder')}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('questionnaire.title')}</h1>
          <p className="text-gray-500 text-sm">{t('questionnaire.instructions')}</p>

          {/* Progress */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-500 mb-2">
              <span>
                {t('questionnaire.question')} {currentIndex + 1} {t('questionnaire.of')} {questions.length}
              </span>
              <span>{Math.round(((currentIndex + 1) / questions.length) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Question */}
        {currentQuestion && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {getLocalizedField(currentQuestion, 'question')}
              {currentQuestion.is_required && <span className="text-red-500 ml-1">*</span>}
            </h2>

            {renderResponseInput(currentQuestion)}

            {showRequiredError && (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {t('questionnaire.answerRequired')}
              </p>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between gap-4">
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('questionnaire.previous')}
          </button>

          {currentIndex < questions.length - 1 ? (
            <button onClick={handleNext} className="btn-primary">
              {t('questionnaire.next')}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-primary disabled:opacity-50"
            >
              {submitting ? t('questionnaire.submitting') : t('questionnaire.submit')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
